import {
  AlignLeftIcon,
  AlignRightIcon,
  CenterAlignHorizontalIcon,
} from '@persona-id/design-tokens/icons';
import { useCallback, useMemo } from 'react';

import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import { ToggleStyleInput } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { StyleValueAlignment, StyleValueJustify } from 'lib/inquiryStyles';
import { LayoutComponent, LayoutComponentConfig, LayoutKeys } from 'lib/interfaces/ui-components';

type LayoutStyleAttributes = {
  alignment: {
    label: string;
    default: StyleValueAlignment;
  };
  justify: {
    label: string;
    default: StyleValueJustify;
  };
};

const DEFAULT_LAYOUT_ATTRIBUTES: LayoutStyleAttributes = {
  alignment: {
    label: 'Alignment',
    default: StyleValueAlignment.Center,
  },
  justify: {
    label: 'Alignment',
    default: StyleValueJustify.Center,
  },
};

const LAYOUT_OPTIONS = {
  alignment: [
    { label: <AlignLeftIcon size={12} />, value: StyleValueAlignment.Start },
    {
      label: <CenterAlignHorizontalIcon size={12} />,
      value: StyleValueAlignment.Center,
    },
    { label: <AlignRightIcon size={12} />, value: StyleValueAlignment.End },
  ],
  justify: [
    { label: <AlignLeftIcon size={12} />, value: StyleValueJustify.Start },
    {
      label: <CenterAlignHorizontalIcon size={12} />,
      value: StyleValueJustify.Center,
    },
    { label: <AlignRightIcon size={12} />, value: StyleValueJustify.End },
  ],
};

type LayoutStylesProps = {
  componentPreview: LayoutComponent;
  component: LayoutComponentConfig;
  styleVariantIndex: number;
  editable: boolean;
  baseComponentConfig: LayoutComponentConfig;
  submitStyle: (field: string, values: any) => void;
};

const LayoutStyles = ({
  componentPreview,
  component,
  styleVariantIndex,
  editable,
  baseComponentConfig,
  submitStyle,
}: LayoutStylesProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const renderableLayoutSettings = Object.keys(baseComponentConfig['style-variants'][0]).filter(
    (key) => key in DEFAULT_LAYOUT_ATTRIBUTES,
  );

  const layoutDefaults: LayoutStyleAttributes = useMemo(() => {
    const layoutMapTuples = Object.entries(DEFAULT_LAYOUT_ATTRIBUTES)
      .filter(([style, _]) => renderableLayoutSettings.includes(style))
      .map((styleAssignment) => {
        const [style, props] = styleAssignment;
        const defaultValue =
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          componentPreview.styles?.[style as LayoutKeys]?.base?.base ?? props.default;
        return [style, { ...props, default: defaultValue }];
      });

    return Object.fromEntries(layoutMapTuples);
  }, [componentPreview.styles, renderableLayoutSettings]);

  const onToggleChange = useCallback(
    (value: StyleValueAlignment | StyleValueJustify, field: string) => {
      submitStyle(field, value);
    },
    [submitStyle],
  );

  return (
    <SectionAccordion title='Layout'>
      {Object.entries(layoutDefaults).map(([styleField, settings]) => {
        return (
          <SectionRow key={styleField}>
            <SectionColumn>
              <ToggleStyleInput
                label={settings.label}
                field={styleField}
                editable={editable}
                name={`layout-${component.name}-${styleField}-${styleVariantIndex}-toggle`}
                defaultValue={settings.default}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                options={LAYOUT_OPTIONS[styleField as LayoutKeys]}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onChange={onToggleChange}
              />
            </SectionColumn>
          </SectionRow>
        );
      })}
    </SectionAccordion>
  );
};

export default LayoutStyles;
